const WEBMCP_INIT_FLAG = "__rkubeWebMcpInitialized";

const ALLOWED_PATHS = [
  "/",
  "/campaign",
  "/cta-landing",
  "/contact",
  "/tax",
  "/books",
  "/company",
  "/compliance",
  "/people",
  "/equity",
  "/about",
  "/blog",
  "/product-sheets",
];

function emitInputEvents(element) {
  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
}

function findFirstCallLink() {
  const selectors = [
    "a[href^='tel:']",
    "[data-call-button='true']",
    ".call-button a[href^='tel:']",
  ];
  for (const selector of selectors) {
    const el = document.querySelector(selector);
    if (el) return el;
  }
  return null;
}

function toSafePath(inputPath) {
  if (typeof inputPath !== "string" || !inputPath.trim()) return null;
  const path = inputPath.startsWith("/") ? inputPath : `/${inputPath}`;
  return ALLOWED_PATHS.includes(path) ? path : null;
}

function createWebMcpTools() {
  return [
    {
      name: "navigate-rkube-page",
      description: "Navigate to a supported RKube site page.",
      inputSchema: {
        type: "object",
        properties: {
          path: {
            type: "string",
            description: "Destination path on rkubeservices.com.",
            enum: ALLOWED_PATHS,
          },
        },
        required: ["path"],
        additionalProperties: false,
      },
      execute: ({ path }) => {
        const safePath = toSafePath(path);
        if (!safePath) {
          return { ok: false, error: "Unsupported path." };
        }
        window.location.assign(safePath);
        return { ok: true, navigatedTo: safePath };
      },
    },
    {
      name: "trigger-rkube-call-intent",
      description: "Trigger the visible click-to-call action on the current page.",
      inputSchema: {
        type: "object",
        properties: {
          requireConfirmation: {
            type: "boolean",
            description: "If true, ask for user confirmation before clicking.",
            default: true,
          },
        },
        additionalProperties: false,
      },
      execute: async ({ requireConfirmation = true } = {}, client) => {
        const callLink = findFirstCallLink();
        if (!callLink) {
          return { ok: false, error: "No call action found on this page." };
        }
        const phone = (callLink.getAttribute("href") || "").replace("tel:", "");
        const clickCall = () => {
          callLink.click();
          return { ok: true, phone, action: "call_click_triggered" };
        };

        if (!requireConfirmation) return clickCall();

        if (client && typeof client.requestUserInteraction === "function") {
          return client.requestUserInteraction(async () => {
            const approved = window.confirm(
              `Start a call${phone ? ` to ${phone}` : ""}?`
            );
            if (!approved) return { ok: false, cancelled: true };
            return clickCall();
          });
        }

        const approved = window.confirm(`Start a call${phone ? ` to ${phone}` : ""}?`);
        if (!approved) return { ok: false, cancelled: true };
        return clickCall();
      },
    },
    {
      name: "fill-rkube-contact-form",
      description: "Fill the visible lead/contact form fields and optionally submit.",
      inputSchema: {
        type: "object",
        properties: {
          name: { type: "string", description: "Lead full name." },
          email: { type: "string", description: "Lead email address." },
          phone: { type: "string", description: "Lead phone number." },
          message: { type: "string", description: "Lead notes or request details." },
          submit: {
            type: "boolean",
            description: "Submit the form after filling fields.",
            default: false,
          },
        },
        additionalProperties: false,
      },
      execute: ({ name, email, phone, message, submit = false } = {}) => {
        const form =
          document.querySelector("form#cta-form") ||
          document.querySelector("form[action*='contact']") ||
          document.querySelector("form");

        if (!form) return { ok: false, error: "No form found." };

        const fields = [
          { value: name, selectors: ["input[name='name']", "#name", "input[autocomplete='name']"], key: "name" },
          { value: email, selectors: ["input[name='email']", "#email", "input[type='email']"], key: "email" },
          { value: phone, selectors: ["input[name='phone']", "#phone", "input[type='tel']"], key: "phone" },
          { value: message, selectors: ["textarea[name='message']", "#message", "textarea"], key: "message" },
        ];

        const filled = [];
        for (const field of fields) {
          if (typeof field.value !== "string") continue;
          let el = null;
          for (const selector of field.selectors) {
            el = form.querySelector(selector);
            if (el) break;
          }
          if (!el) continue;
          el.value = field.value;
          emitInputEvents(el);
          filled.push(field.key);
        }

        let submitted = false;
        if (submit) {
          if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
            submitted = true;
          } else {
            const submitBtn = form.querySelector("button[type='submit'], input[type='submit']");
            if (submitBtn) {
              submitBtn.click();
              submitted = true;
            }
          }
        }

        return { ok: true, filled, submitted };
      },
    },
    {
      name: "read-rkube-page-context",
      description: "Read key page context for planning the next interaction.",
      inputSchema: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
      annotations: {
        readOnlyHint: true,
      },
      execute: () => {
        return {
          ok: true,
          title: document.title,
          path: window.location.pathname,
          hasCallButton: Boolean(findFirstCallLink()),
          hasForm: Boolean(document.querySelector("form")),
          timestamp: new Date().toISOString(),
        };
      },
    },
  ];
}

export function initializeWebMcp() {
  if (window[WEBMCP_INIT_FLAG]) return;

  const modelContext = navigator?.modelContext;
  if (!modelContext) return;

  const tools = createWebMcpTools();
  try {
    if (typeof modelContext.provideContext === "function") {
      modelContext.provideContext({ tools });
      window[WEBMCP_INIT_FLAG] = true;
      return;
    }

    if (typeof modelContext.registerTool === "function") {
      tools.forEach((tool) => modelContext.registerTool(tool));
      window[WEBMCP_INIT_FLAG] = true;
    }
  } catch (error) {
    console.warn("WebMCP registration failed:", error);
  }
}
